import GlobalFeedback from "../../../../../global/GlobalFeedback";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import PaymentStrip from "../../../../payment/PaymentStrip";
import {
  formatDisplayDate,
  formatMoney,
  slotsLabel,
  TRANSACTION_ID_MAX_LENGTH,
} from "../tournamentRegistrationHelpers";

export default function TournamentRegistrationFormSection({
  panelRef,
  registrationFormRef,
  formStep,
  setFormStep,
  step1Complete,
  step2Complete,
  requiresPayment,
  selectedTournament,
  studentInput,
  handleStudentInputChange,
  fieldErrors,
  searchResults,
  suggestLoading,
  suggestionsOpen,
  typeaheadRef,
  handleSelectStudent,
  studentCode,
  studentName,
  verifiedMemberships,
  selectedMembershipId,
  setSelectedMembershipId,
  setMembershipLabel,
  membershipLabel,
  studentId,
  paymentType,
  paymentTransactionId,
  onPaymentTypeChange,
  onPaymentTransactionIdChange,
  phonePeCompleted,
  phonePeError,
  onPhonePeError,
  onBeforePayRedirect,
  onClearOnlinePayment,
  paymentProof,
  paymentProofFileName,
  onPaymentProofChange,
  formError,
  handleSubmit,
  handleBackFromPaymentStep,
  canSubmitAtCurrentStep,
  submitting,
  submitAmount,
}) {
  return (
    <div ref={panelRef} className="treg-panel">
      <h3 className="treg-panel__title">Registration</h3>
      <p className="treg-panel__hint">
        Use the steps below in order. Start by choosing a tournament card on the left.
      </p>

      <nav className="treg-stepper" aria-label="Registration steps">
        {[
          { n: 1, label: "Tournament", done: step1Complete },
          { n: 2, label: "Student", done: step2Complete },
          { n: 3, label: requiresPayment ? "Payment" : "Submit", done: false },
        ].map(({ n, label, done }) => (
          <div
            key={n}
            className={`treg-stepper__item${formStep === n ? " treg-stepper__item--active" : ""}${
              done ? " treg-stepper__item--done" : ""
            }`}
          >
            <div className="treg-stepper__circle" aria-hidden>
              {done && formStep !== n ? "✓" : n}
            </div>
            <span className="treg-stepper__label">{label}</span>
          </div>
        ))}
      </nav>

      <form ref={registrationFormRef} className="treg-form-grid" onSubmit={handleSubmit}>
        <div
          className={`treg-step-section${formStep === 1 ? " treg-step-section--open" : ""}${
            step1Complete ? " treg-step-section--done" : ""
          }`}
        >
          <div className="treg-step-section__head">
            <span>
              <span className="treg-step-section__num">1</span>
              Tournament
            </span>
          </div>
          <div className="treg-step-section__body">
            {!selectedTournament ? (
              <p className="treg-step-summary" style={{ margin: 0 }}>
                Select <strong>Register student</strong> on a tournament card to lock in the event.
              </p>
            ) : (
              <>
                <p className="treg-step-summary">
                  <strong>{selectedTournament.tournament_name}</strong>
                  <br />
                  {formatDisplayDate(selectedTournament.tournament_date)}
                  {" · "}
                  {selectedTournament.tournament_location || "Venue TBC"}
                  <br />
                  Fee {formatMoney(selectedTournament.participation_fee)} · Slots{" "}
                  {slotsLabel(selectedTournament)}
                </p>
                {formStep === 1 ? (
                  <div className="treg-btn-row">
                    <GlobalButton type="button" variant="primary" onClick={() => setFormStep(2)}>
                      Continue to student
                    </GlobalButton>
                  </div>
                ) : (
                  <p
                    className="treg-step-summary"
                    style={{ margin: 0, fontSize: "0.75rem", color: "#64748b" }}
                  >
                    Tournament confirmed — use Back on the next step to change the list selection.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div
          className={`treg-step-section${formStep === 2 ? " treg-step-section--open" : ""}${
            step2Complete ? " treg-step-section--done" : ""
          }${formStep < 2 ? " treg-step-section--locked" : ""}`}
        >
          <div className="treg-step-section__head">
            <span>
              <span className="treg-step-section__num">2</span>
              Student
            </span>
          </div>
          <div className="treg-step-section__body">
            <div className="student-pay-student-typeahead" ref={typeaheadRef}>
              <GlobalTextField
                label="Search student"
                value={studentInput}
                onChange={handleStudentInputChange}
                placeholder="Type name or student code"
                error={fieldErrors.student_id}
                disabled={formStep < 2 || !selectedTournament}
                autoComplete="off"
              />
              {suggestionsOpen && (searchResults.length > 0 || suggestLoading) ? (
                <div className="student-search-results student-pay-student-typeahead__dropdown">
                  {suggestLoading ? (
                    <div className="student-search-result-item">Searching…</div>
                  ) : (
                    searchResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="student-search-result-item"
                        style={{
                          width: "100%",
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                        }}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => handleSelectStudent(s)}
                      >
                        <div className="student-search-result-info">
                          <strong>{s.full_name}</strong>
                          <span>{s.code || `ID ${s.id}`}</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>

            <div className="treg-form-grid treg-form-grid--2">
              <GlobalTextField label="Student ID (code)" value={studentCode} onChange={() => {}} disabled />
              <GlobalTextField label="Student name" value={studentName} onChange={() => {}} disabled />
            </div>

            {verifiedMemberships.length > 1 ? (
              <GlobalSelectField
                label="Membership"
                value={selectedMembershipId}
                onChange={(v) => {
                  setSelectedMembershipId(v);
                  const picked = verifiedMemberships.find((m) => String(m.membership_id) === String(v));
                  setMembershipLabel(picked?.membership_name || "");
                }}
                options={verifiedMemberships.map((m) => ({
                  value: String(m.membership_id),
                  label: m.membership_name || `Membership ${m.membership_id}`,
                }))}
                disabled={formStep < 2 || !selectedTournament}
              />
            ) : (
              <GlobalTextField
                label="Membership"
                value={membershipLabel}
                onChange={() => {}}
                disabled
                placeholder="—"
              />
            )}

            {formError && !requiresPayment ? (
              <div className="treg-form-error-above-submit">
                <GlobalFeedback type="error" text={formError} />
              </div>
            ) : null}

            <div className="treg-btn-row treg-actions--between">
              <GlobalButton type="button" variant="ghost" disabled={formStep < 2 || submitting} onClick={() => setFormStep(1)}>
                Back
              </GlobalButton>
              <GlobalButton
                type="button"
                variant="primary"
                disabled={formStep < 2 || !studentId || submitting}
                onClick={() => {
                  if (requiresPayment) setFormStep(3);
                  else registrationFormRef.current?.requestSubmit();
                }}
              >
                {requiresPayment
                  ? "Continue to payment"
                  : submitting
                    ? "Submitting…"
                    : "Submit registration"}
              </GlobalButton>
            </div>
          </div>
        </div>

        {requiresPayment ? (
          <div
            className={`treg-step-section${formStep === 3 ? " treg-step-section--open" : ""}${
              formStep < 3 ? " treg-step-section--locked" : ""
            }`}
          >
            <div className="treg-step-section__head">
              <span>
                <span className="treg-step-section__num">3</span>
                Payment
              </span>
            </div>
            <div className="treg-step-section__body">
              <GlobalTextField
                label="Amount"
                value={submitAmount > 0 ? formatMoney(submitAmount) : "0"}
                onChange={() => {}}
                disabled
              />

              <PaymentStrip
                paymentType={paymentType}
                onPaymentTypeChange={onPaymentTypeChange}
                receiptValue={paymentTransactionId}
                onReceiptChange={(v) =>
                  onPaymentTransactionIdChange(String(v).slice(0, TRANSACTION_ID_MAX_LENGTH))
                }
                receiptError={fieldErrors.payment_transaction_id || fieldErrors.transaction_id}
                amount={submitAmount}
                phonePeCompleted={phonePeCompleted}
                phonePeErrorMsg={phonePeError}
                onPhonePeError={onPhonePeError}
                formErrorDetails={fieldErrors}
                formSubmitting={submitting || formStep < 3}
                alertId="tournament-reg-payment"
                onBeforePayRedirect={onBeforePayRedirect}
                onClearOnlinePayment={onClearOnlinePayment}
                receiptMaxLength={TRANSACTION_ID_MAX_LENGTH}
                proofUrl={paymentProof}
                proofFileName={paymentProofFileName}
                proofError={fieldErrors.proof}
                onProofChange={onPaymentProofChange}
              />

              {formError ? (
                <div className="treg-form-error-above-submit">
                  <GlobalFeedback type="error" text={formError} />
                </div>
              ) : null}

              <div className="treg-actions treg-actions--between">
                <GlobalButton
                  type="button"
                  variant="ghost"
                  disabled={formStep < 3}
                  onClick={handleBackFromPaymentStep}
                >
                  Back
                </GlobalButton>
                <GlobalButton
                  type="submit"
                  variant="primary"
                  disabled={!canSubmitAtCurrentStep || !selectedTournament || submitting}
                >
                  {submitting ? "Submitting…" : "Submit registration"}
                </GlobalButton>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </div>
  );
}
